'use client';

import React, { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';

import getSearchParams from '@/app/components/search/common/getSearchParams';
import { URLParameters } from '@/app/common/urlParameters';
import { findMealPlan, getMealPlanCode } from '@/app/components/hotel-detail/rooms/roomCommon';
import { getAvailableCancellationMealPlans } from '@/app/components/hotel-detail/rooms/RoomPanelCommon';
import { selectCheapestBoardClassTarget } from '@/app/components/hotel-detail/evo/boardClassSelection';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import type { BrandedFare } from '@/app/api/hotel-detail/branded-fares/route';
import { useOffersContext } from './OffersProvider';
import { useUrlSearchParams } from './UrlSearchParamsProvider';

export type SelectedMealPlanContextProps = {
  selectedMealPlan: MealPlanType | undefined;
  selectedRoom: RoomType | undefined;
  selectedBrandedFare: BrandedFare | undefined;
  setSelectedRoom: React.Dispatch<React.SetStateAction<RoomType | undefined>>;
  setSelectedMealPlan: React.Dispatch<React.SetStateAction<MealPlanType | undefined>>;
  setSelectedBrandedFare: React.Dispatch<React.SetStateAction<BrandedFare | undefined>>;
  resetSelectedCancellationToDefault: () => MealPlanType | undefined;
  // MPH-1366: whether the selected room is one the user picked (a room card's "Select room" or "Continue",
  // the room details modal's "Continue", or a room restored from the URL) rather than the page's own
  // preselection. Read by the evo board class switch, which keeps only a room the user picked.
  isRoomPickedByUser: boolean;
  setIsRoomPickedByUser: React.Dispatch<React.SetStateAction<boolean>>;
};

type SelectedMealPlanProviderProps = {
  children: ReactNode;
};

/**
 *
 * Problem: The selection of room and meal plan differs between TSX and curated content flows:
 *
 * - In TSX, the selected room and meal plan are managed via URL parameters, allowing navigation steps to persist selections in the URL.
 * - In curated content, the selected meal plan is managed in React context and only added to the URL at the package review step.
 *
 * Solution: This provider unifies both flows by:
 *
 * - Storing the selected room and meal plan in context, so components can access them regardless of the flow.
 * - On initialization, checking the URL for parameters (for TSX) and falling back to context state (for curated content).
 * - Ensuring that changes in available offers (e.g., due to filters or transport changes) preserve the user's selection when possible.
 *
 * This approach allows all components to use a single context for room and meal plan selection, supporting both navigation patterns.
 *
 * */
export const SelectedMealPlanContext = createContext<SelectedMealPlanContextProps>({
  selectedMealPlan: undefined,
  selectedRoom: undefined,
  selectedBrandedFare: undefined,
  setSelectedRoom: () => null,
  setSelectedMealPlan: () => null,
  setSelectedBrandedFare: () => null,
  resetSelectedCancellationToDefault: () => undefined,
  isRoomPickedByUser: false,
  setIsRoomPickedByUser: () => null,
});

export function SelectedMealPlanProvider(props: SelectedMealPlanProviderProps) {
  const { children } = props;

  const searchParams = getSearchParams();
  const { offersData, isLoadingRooms } = useOffersContext();
  const { checkin, checkout, datesOrigin, mealPlanFilterValues } = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const evolutionEnabled = localization.configValuesApp?.evolutionEnabled;

  const [selectedMealPlan, setSelectedMealPlan] = useState<MealPlanType | undefined>(undefined);
  const [selectedRoom, setSelectedRoom] = useState<RoomType | undefined>(undefined);
  const [selectedBrandedFare, setSelectedBrandedFare] = useState<BrandedFare | undefined>(undefined);
  const [isRoomPickedByUser, setIsRoomPickedByUser] = useState(false);

  // Apex calendar date changes discard the previous selection: the new offers must surface
  // the default (cheapest) room and meal plan so the page price matches the price shown in
  // the calendar, instead of re-targeting the old room below. Keyed on the dates only —
  // origin alone changing (e.g. picking the current dates from the calendar) triggers no
  // offers refetch, so resetting then would leave the selection empty.
  useEffect(() => {
    if (datesOrigin === 'APEX_CALENDAR') {
      setSelectedRoom(undefined);
      setSelectedMealPlan(undefined);
      setIsRoomPickedByUser(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkin, checkout]);

  useEffect(() => {
    // While rooms are being refetched, offersData still holds the previous offers (and its
    // identity churns with isLoadingRooms). Re-seeding from them would target stale offers —
    // and resurrect, via this effect's stale closure, the selection that the apex-calendar
    // reset above just cleared. The fetch always lands with a new offersData, re-running this.
    if (isLoadingRooms) return;

    const roomOfferIdFromUrl = searchParams[URLParameters.ROOM_ID];
    const mealPlanIdFromUrl = searchParams[URLParameters.MEALPLAN_ID];
    const mealPlanCode = searchParams[URLParameters.MEALPLAN_CODE];

    // When using prefilters in the URL, these filters can be removed, causing a new offersData array with all the offers.
    // We need to keep the selected meal plan in this case.
    // Same for change transport: we need to keep the previously selected meal plan, but in this case there is a new rooms call
    // and meal plan IDs are generated by the backend again. We need to target the previously selected meal plan by meal plan code.
    if (offersData?.rooms && offersData.rooms.length > 0) {
      // Evo: a meal-plan URL filter preselects the cheapest room+rate of that board class (the list is
      // not filtered — see OffersProvider). Skipped once the user has a selection or the URL carries an
      // explicit room/meal plan, so navigation selections always win. Also skipped after an apex-calendar
      // date change: that navigation restarts from the overall cheapest offer so the page price matches the
      // calendar price (which is unfiltered by this meal filter), so the filter must not re-narrow it here.
      const mealFilterPreselect =
        evolutionEnabled &&
        datesOrigin !== 'APEX_CALENDAR' &&
        mealPlanFilterValues.length &&
        !selectedRoom &&
        !roomOfferIdFromUrl &&
        !mealPlanIdFromUrl
          ? selectCheapestBoardClassTarget(offersData.rooms, mealPlanFilterValues as BoardClassType[])
          : undefined;

      const targetRoom =
        mealFilterPreselect?.room ??
        (selectedRoom
          ? // roomOfferId first: on multi-room searches the same roomId is listed once per
            // rooms-included-in-price count, so matching roomId alone can re-seed the selection onto the
            // other variant and silently re-price the page (MPH-882). roomId stays as the fallback for
            // offers whose rooms-included count changed between calls.
            (offersData.rooms.find((r) => r.roomOfferId === selectedRoom.roomOfferId) ??
            offersData.rooms.find((r) => r.roomId === selectedRoom.roomId) ??
            offersData.rooms[0])
          : roomOfferIdFromUrl
            ? (offersData.rooms.find((room) => room.roomOfferId === roomOfferIdFromUrl) ?? offersData.rooms[0])
            : offersData.rooms[0]);

      const targetMealPlan =
        mealFilterPreselect?.mealPlan ??
        (selectedMealPlan
          ? findMealPlan(targetRoom.configurations, selectedMealPlan.id, getMealPlanCode(selectedMealPlan))
          : mealPlanIdFromUrl
            ? findMealPlan(targetRoom.configurations, mealPlanIdFromUrl, mealPlanCode)
            : targetRoom.configurations[targetRoom.selectedMealPlanIndex]) ??
        targetRoom.configurations[0];

      const isUrlRoom = Boolean(roomOfferIdFromUrl) && targetRoom.roomOfferId === roomOfferIdFromUrl;

      // MPH-1366: the user's pick survives the seeding only when the seeding kept that same room; a fallback to
      // another room is the page's own choice. Without a previous selection, a room restored from the URL counts
      // as the user's pick, because only a Continue writes roomId into the URL. A meal-filter preselection only
      // runs without a previous selection and without a URL roomId, so it takes that branch as the page's choice.
      if (selectedRoom) {
        const isSameRoom =
          targetRoom.roomOfferId === selectedRoom.roomOfferId || targetRoom.roomId === selectedRoom.roomId;
        if (!isSameRoom) setIsRoomPickedByUser(false);
      } else {
        setIsRoomPickedByUser(isUrlRoom);
      }

      setSelectedRoom(targetRoom);
      setSelectedMealPlan(targetMealPlan);
    }
    // Intentionally re-seeds only when offers change (not on selection/filter edits) — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offersData, isLoadingRooms]);

  const memoizedValue = useMemo(
    () => ({
      selectedMealPlan,
      setSelectedMealPlan,
      selectedRoom,
      setSelectedRoom,
      selectedBrandedFare,
      setSelectedBrandedFare,
      // Resets only the cancellation choice: keeps the selected room, board class and (when possible)
      // bed/promotion, switching to the cheapest cancellation option offered — the option the
      // cancellation modal lists first. Used by the hotel "Modify" link on the flights step, so going
      // back to the rooms page drops the cancellation upsell but keeps the user's room and board choice.
      // Returns the reset meal plan so callers can persist it (e.g. in the URL).
      resetSelectedCancellationToDefault: () => {
        if (!selectedRoom || !selectedMealPlan) return undefined;
        const cancellationMealPlans = getAvailableCancellationMealPlans(selectedRoom.configurations, selectedMealPlan);
        if (cancellationMealPlans.length === 0) return undefined;
        const cheapest = cancellationMealPlans.reduce((min, plan) => (plan.finalPrice < min.finalPrice ? plan : min));
        setSelectedMealPlan(cheapest);
        return cheapest;
      },
      isRoomPickedByUser,
      setIsRoomPickedByUser,
    }),
    [selectedMealPlan, selectedRoom, selectedBrandedFare, isRoomPickedByUser]
  );

  return <SelectedMealPlanContext.Provider value={memoizedValue}>{children}</SelectedMealPlanContext.Provider>;
}

/**
 * returns the current page status
 */
export function useSelectedMealPlan(): SelectedMealPlanContextProps {
  const context = useContext(SelectedMealPlanContext);

  if (context === undefined) {
    throw new Error('useSelectedMealPlan must be within a SelectedMealPlanProvider');
  }

  return context;
}
